import React, { useEffect, useRef, useState } from 'react';
import { annotationsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Check, MessageSquarePlus, Trash2, AlertCircle, Highlighter } from 'lucide-react';
import { toast } from 'sonner';

// Cross-component event used by the inline selection toolbar to push a quoted
// passage into the annotations panel without prop-drilling.
export const ANNOTATION_QUOTE_EVENT = 'nn-annotation-quote';

const TYPE_META = {
  comment:    { color: '#1E5A8A', icon: MessageSquarePlus, label: 'Comment' },
  highlight:  { color: '#B89B5E', icon: Highlighter,        label: 'Highlight' },
  suggestion: { color: '#1F7A5A', icon: Check,              label: 'Suggestion' },
  legal:      { color: '#B3261E', icon: AlertCircle,        label: 'Legal' },
};

export default function AnnotationsPanel({ articleId, onCountChange }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [annotationType, setAnnotationType] = useState('comment');
  const [quote, setQuote] = useState('');
  const [loading, setLoading] = useState(false);
  const noteFieldRef = useRef(null);
  const formRef = useRef(null);

  const refresh = React.useCallback(async () => {
    if (!articleId) return;
    try {
      const r = await annotationsAPI.list(articleId);
      const list = r.data?.annotations || [];
      setItems(list);
      onCountChange && onCountChange(list.filter((a) => !a.is_resolved).length);
    } catch {}
  }, [articleId, onCountChange]);

  useEffect(() => { refresh(); }, [refresh]);

  // Listen for inline-selection events fired by InlineSelectionToolbar.
  // The handler pre-fills the quote textarea with the highlighted passage,
  // optionally swaps the annotation type, and scrolls the form into view.
  useEffect(() => {
    function onQuote(e) {
      const detail = e?.detail || {};
      if (!detail.quote) return;
      setQuote(detail.quote.slice(0, 500));
      if (detail.annotation_type && TYPE_META[detail.annotation_type]) {
        setAnnotationType(detail.annotation_type);
      }
      requestAnimationFrame(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        noteFieldRef.current?.focus();
      });
    }
    window.addEventListener(ANNOTATION_QUOTE_EVENT, onQuote);
    return () => window.removeEventListener(ANNOTATION_QUOTE_EVENT, onQuote);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setLoading(true);
    try {
      await annotationsAPI.create(articleId, {
        note: text.trim(),
        annotation_type: annotationType,
        quoted_text: quote.trim() || null,
      });
      setText('');
      setQuote('');
      toast.success('Annotation added');
      refresh();
    } catch {
      toast.error('Failed to add annotation');
    } finally { setLoading(false); }
  };

  const toggleResolved = async (a) => {
    try {
      await annotationsAPI.update(a.id, { is_resolved: !a.is_resolved });
      refresh();
    } catch {}
  };

  const remove = async (a) => {
    try {
      await annotationsAPI.delete(a.id);
      refresh();
    } catch {}
  };

  return (
    <div className="space-y-3" data-testid="annotations-panel">
      <form onSubmit={submit} className="space-y-2" ref={formRef}>
        <Select value={annotationType} onValueChange={setAnnotationType}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(TYPE_META).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={quote}
          onChange={(e) => setQuote(e.target.value)}
          placeholder='Quoted passage (optional)…'
          className="text-xs"
        />
        <Textarea
          value={text}
          ref={noteFieldRef}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a comment or suggestion…"
          rows={3}
          className="text-sm"
          data-testid="annotation-input"
        />
        <Button
          size="sm"
          disabled={loading || !text.trim()}
          className="bg-[#121316] text-white hover:bg-[#1B1D22] w-full"
          type="submit"
          data-testid="annotation-submit"
        >
          <MessageSquarePlus className="w-3.5 h-3.5 mr-1" /> Add Annotation
        </Button>
      </form>

      <div className="space-y-2 max-h-[420px] overflow-auto pr-1">
        {items.length === 0 && (
          <p className="text-xs text-[#5B616E] py-3 text-center">No annotations yet.</p>
        )}
        {items.map((a) => {
          const meta = TYPE_META[a.annotation_type] || TYPE_META.comment;
          const Icon = meta.icon;
          return (
            <div
              key={a.id}
              className={`rounded-lg border p-2.5 ${a.is_resolved ? 'opacity-60' : ''}`}
              style={{ borderColor: meta.color + '40', background: meta.color + '08' }}
              data-testid="annotation-item"
            >
              <div className="flex items-start gap-2">
                <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: meta.color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{meta.label}</Badge>
                    <span className="text-[10px] text-[#5B616E]">{a.author_email}</span>
                    {a.is_resolved && <Badge className="text-[10px] bg-[#1F7A5A]/15 text-[#1F7A5A]">Resolved</Badge>}
                  </div>
                  {a.quoted_text && (
                    <blockquote className="text-[11px] italic border-l-2 pl-2 my-1 text-[#5B616E]" style={{ borderColor: meta.color }}>
                      “{a.quoted_text}”
                    </blockquote>
                  )}
                  <p className="text-xs leading-relaxed">{a.note}</p>
                  <p className="text-[10px] text-[#5B616E] mt-1">
                    {new Date(a.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => toggleResolved(a)}
                    className="p-1 rounded hover:bg-white/70"
                    title={a.is_resolved ? 'Reopen' : 'Resolve'}
                    data-testid="annotation-resolve"
                  >
                    <Check className="w-3 h-3 text-[#1F7A5A]" />
                  </button>
                  <button
                    onClick={() => remove(a)}
                    className="p-1 rounded hover:bg-white/70"
                    title="Delete"
                  >
                    <Trash2 className="w-3 h-3 text-[#B3261E]" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
